/*
 * "Join now" pop-up (PROD-9977): at session end minus 10 minutes, tells the
 * allocated rep (and the task holder, if different) to join. Mounted in the app
 * shell like the instant alert; polls join_now, shows one prompt at a time.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { fetch_me_once as fetchMeOnce } from '../claim_api';
import { publish_claim_changed as publishClaimChanged } from '../claim_broadcast';
import { Btn, Chip, Ledger } from '../ui';
import { errorStatus, fetchJoinNow, sendPrompt } from './api';
import {
  JOIN_POLL_MS,
  joinLinks,
  joinStatus,
  mayTakeFocus,
  shouldRefetch,
  mayPollJoinNow,
  nextPrompt,
  promptKey,
  promptCopy,
  pruneHandled
} from './join_now';
import type { JoinStatus } from './join_now';
import type { JoinNowItem } from './types';

const TITLE_ID = 'join-now-title';
const BODY_ID = 'join-now-body';
// How long the claim status stays before the card goes away on its own.
const STATUS_MS = 15 * 1000;

const CARD_STYLE: React.CSSProperties = {
  top: 24,
  right: 16,
  width: 420,
  maxWidth: 'calc(100vw - 32px)',
  zIndex: 1250,
  border: '2px solid var(--brand-primary)',
  boxShadow: 'var(--shadow-modal)'
};
const CARD_CLASS =
  'stl-root fixed flex flex-col gap-2.5 rounded-xl outline-none bg-surface-card px-[18px] py-4 text-[13px] text-content-primary';

// Fire-and-forget: a lost event must never block the rep. A 403 means the caller is no longer
// allocated or holding the task, so that prompt is dropped quietly.
const report = (
  uuid: string,
  event: 'shown' | 'opened' | 'dismissed',
  onForbidden: () => void,
  reason?: 'cant_join'
) => {
  sendPrompt(uuid, event, reason).catch((e: unknown) => {
    if (errorStatus(e) === 403) onForbidden();
  });
};

function JoinNowPopup() {
  const [enabled, setEnabled] = useState(false);
  const [items, setItems] = useState<JoinNowItem[]>([]);
  const [handled, setHandled] = useState<Set<string>>(() => new Set());
  const [clock, setClock] = useState(() => Date.now());
  const [announce, setAnnounce] = useState('');
  // The claim result after "Join session", shown in place of the prompt.
  const [status, setStatus] = useState<JoinStatus | null>(null);
  const shownRef = useRef(new Set<string>());
  const seqRef = useRef(0);
  const lastPollRef = useRef(0);
  const cardRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    let live = true;
    fetchMeOnce()
      .then((me) => live && setEnabled(mayPollJoinNow(me)))
      .catch(() => {});
    return () => {
      live = false;
    };
  }, []);

  const poll = useCallback(() => {
    if (document.visibilityState === 'hidden') return;
    lastPollRef.current = Date.now();
    seqRef.current += 1;
    const seq = seqRef.current;
    fetchJoinNow()
      .then((next) => {
        if (seq !== seqRef.current) return;
        setItems(next);
        setHandled((prev) => pruneHandled(prev, next));
        // A prompt the server stopped listing reports shown again if it comes back.
        shownRef.current = pruneHandled(shownRef.current, next);
        setClock(Date.now());
      })
      // Silent: this runs every 30 s for every rep; the next poll retries.
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (!enabled) return undefined;
    poll();
    const id = setInterval(poll, JOIN_POLL_MS);
    const onReturn = () => {
      if (shouldRefetch(lastPollRef.current, Date.now())) poll();
    };
    document.addEventListener('visibilitychange', onReturn);
    window.addEventListener('focus', onReturn);
    return () => {
      clearInterval(id);
      document.removeEventListener('visibilitychange', onReturn);
      window.removeEventListener('focus', onReturn);
    };
  }, [enabled, poll]);

  const current = enabled ? nextPrompt(items, handled, clock) : null;

  const drop = useCallback((key: string) => setHandled((prev) => new Set(prev).add(key)), []);

  useEffect(() => {
    if (!current) return;
    const key = promptKey(current);
    if (shownRef.current.has(key)) return;
    shownRef.current.add(key);
    report(current.session_uuid, 'shown', () => drop(key));
  }, [current, drop]);

  // Focus the card when it appears, unless the rep is typing; then announce it instead.
  const currentUuid = current ? promptKey(current) : '';
  const currentTitle = current ? `Join now: ${current.student_name}` : '';
  useEffect(() => {
    if (!currentUuid) return;
    const activeEl = document.activeElement as HTMLElement | null;
    // Any open modal (SalesDialog sets aria-modal) or focus inside another dialog keeps its focus.
    const host = activeEl?.closest?.('[role="dialog"],[role="alertdialog"]');
    const dialogOpen =
      !!document.querySelector('[aria-modal="true"]') || (!!host && host !== cardRef.current);
    if (mayTakeFocus(activeEl, dialogOpen)) {
      setAnnounce('');
      cardRef.current?.focus();
    } else setAnnounce(currentTitle);
  }, [currentUuid, currentTitle]);

  useEffect(() => {
    if (!status) return undefined;
    const id = setTimeout(() => setStatus(null), STATUS_MS);
    return () => clearTimeout(id);
  }, [status]);

  if (status) {
    return (
      <div role="status" data-testid="join-now-status" className={CARD_CLASS} style={CARD_STYLE}>
        {status.tone !== 'neutral' ? (
          <Chip tone={status.tone}>{status.tone === 'success' ? 'Claimed' : 'Not claimed'}</Chip>
        ) : null}
        <p className="m-0 leading-[1.5]">{status.text}</p>
        <div className="flex flex-wrap items-center justify-end gap-2.5">
          {status.task_href ? (
            <Link
              to={status.task_href}
              target="_blank"
              rel="noopener noreferrer"
              className="mr-auto inline-flex min-h-[44px] items-center text-[12.5px] font-semibold text-content-link hover:underline"
            >
              Open task
            </Link>
          ) : null}
          <Btn variant="neutral" size="touch" onClick={() => setStatus(null)}>
            Close
          </Btn>
        </div>
      </div>
    );
  }

  if (!current) return null;

  const copy = promptCopy(current, clock);
  const uuid = current.session_uuid;
  const links = joinLinks(current);
  const key = promptKey(current);
  const done = () => drop(key);

  // Open the tab first, inside the click, so popup blockers allow it; then record and claim.
  const join = () => {
    const item = current;
    window.open(links.join, '_blank', 'noopener,noreferrer');
    done();
    sendPrompt(uuid, 'opened')
      .then((res) => {
        if (res.claimed) publishClaimChanged(res.task_uuid || item.task_uuid || '');
        setStatus(joinStatus(res, item));
      })
      .catch((e: unknown) => {
        // 403: no longer this rep's session; say nothing.
        if (errorStatus(e) !== 403) setStatus(joinStatus(null, item));
      });
  };

  const cantJoin = () => {
    report(uuid, 'dismissed', done, 'cant_join');
    done();
  };

  // No backdrop: the page around it stays usable, like the instant alert.
  return (
    <div
      ref={cardRef}
      tabIndex={-1}
      role="alertdialog"
      aria-labelledby={TITLE_ID}
      aria-describedby={BODY_ID}
      data-testid="join-now-popup"
      className={CARD_CLASS}
      style={CARD_STYLE}
    >
      <span className="sr-only" aria-live="assertive">
        {announce}
      </span>
      <div className="flex flex-wrap items-center gap-2">
        <Chip tone="error">Join now</Chip>
        <span className="text-xs text-content-muted">{copy.when}</span>
      </div>
      <h2 id={TITLE_ID} className="m-0 text-base font-bold leading-6">
        {copy.title}
      </h2>
      <p id={BODY_ID} className="m-0 leading-[1.5] text-content-secondary">
        {copy.reason}
      </p>
      <Ledger
        rows={[
          ['Teacher', current.teacher_name || '—'],
          ...(copy.student_time ? [['Student time', copy.student_time] as [string, string]] : []),
          ['Task', copy.task_line]
        ]}
      />
      <div className="flex flex-wrap items-center justify-end gap-2.5 max-sm:[&>button]:flex-1">
        {links.task ? (
          <Link
            to={links.task}
            target="_blank"
            rel="noopener noreferrer"
            className="mr-auto inline-flex min-h-[44px] items-center text-[12.5px] font-semibold text-content-link hover:underline max-sm:w-full"
          >
            Open task
          </Link>
        ) : null}
        <Btn variant="neutral" size="touch" onClick={cantJoin}>
          Can&apos;t join
        </Btn>
        <Btn variant="primary" size="touch" onClick={join}>
          Join session
        </Btn>
      </div>
    </div>
  );
}

export default JoinNowPopup;
